import { useEffect, useMemo, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { CreditCard, Lock } from 'lucide-react';
import { api } from '@/lib/api';

interface StripeCheckoutProps {
  payload: Record<string, unknown>;
  formValid: boolean;
  onSuccess: (result: { folio: string; id_venta: number }) => void;
  onError: (message: string) => void;
}

function StripePayForm({
  payload,
  clientSecret,
  onSuccess,
  onError,
}: {
  payload: Record<string, unknown>;
  clientSecret: string;
  onSuccess: StripeCheckoutProps['onSuccess'];
  onError: StripeCheckoutProps['onError'];
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [procesando, setProcesando] = useState(false);

  const handlePay = async () => {
    if (!stripe || !elements) return;
    setProcesando(true);

    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        clientSecret,
        confirmParams: {
          return_url: window.location.origin + '/checkout',
        },
        redirect: 'if_required',
      });

      if (error) {
        onError(error.message ?? 'Error al procesar el pago');
        return;
      }

      if (paymentIntent?.status === 'succeeded') {
        const result = await api.stripeConfirm({
          ...payload,
          payment_intent_id: paymentIntent.id,
        });
        onSuccess({ folio: result.folio, id_venta: result.id_venta });
      } else {
        onError('El pago no se completó. Intente de nuevo.');
      }
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Error de pago');
    } finally {
      setProcesando(false);
    }
  };

  return (
    <div className="space-y-4">
      <PaymentElement options={{ layout: 'tabs' }} />
      <button
        type="button"
        onClick={handlePay}
        disabled={!stripe || procesando}
        className="btn-primary w-full"
      >
        <CreditCard className="h-4 w-4" />
        {procesando ? 'Procesando pago...' : 'Pagar ahora'}
      </button>
    </div>
  );
}

export function StripeCheckoutSection({
  payload,
  formValid,
  onSuccess,
  onError,
}: StripeCheckoutProps) {
  const [config, setConfig] = useState<{
    enabled: boolean;
    publishableKey: string;
  } | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [preparando, setPreparando] = useState(false);

  useEffect(() => {
    api
      .stripeConfig()
      .then(setConfig)
      .catch(() => setConfig({ enabled: false, publishableKey: '' }));
  }, []);

  const stripePromise = useMemo(
    () => (config?.publishableKey ? loadStripe(config.publishableKey) : null),
    [config?.publishableKey],
  );

  const iniciarPago = async () => {
    if (!formValid) {
      onError('Complete todos los campos obligatorios antes de pagar.');
      return;
    }
    setPreparando(true);
    onError('');
    try {
      const data = await api.stripeCreateIntent(payload);
      setClientSecret(data.clientSecret);
    } catch (err) {
      onError(err instanceof Error ? err.message : 'No se pudo iniciar el pago');
    } finally {
      setPreparando(false);
    }
  };

  if (!config) {
    return <p className="text-sm text-surface-800/50">Verificando Stripe...</p>;
  }

  if (!config.enabled) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
        <strong>Stripe aún no configurado.</strong>
        <p className="mt-1">
          Copie <code className="text-xs">stripe.sample.php</code> como{' '}
          <code className="text-xs">stripe.local.php</code> y pegue sus keys de{' '}
          <a
            href="https://dashboard.stripe.com/test/apikeys"
            target="_blank"
            rel="noreferrer"
            className="underline"
          >
            dashboard.stripe.com
          </a>
          .
        </p>
      </div>
    );
  }

  if (!clientSecret) {
    return (
      <button
        type="button"
        onClick={iniciarPago}
        disabled={!formValid || preparando}
        className="btn-primary w-full"
      >
        <Lock className="h-4 w-4" />
        {preparando ? 'Preparando pago seguro...' : 'Pagar con tarjeta (Stripe)'}
      </button>
    );
  }

  if (!stripePromise) return null;

  return (
    <Elements stripe={stripePromise} options={{ clientSecret, locale: 'es' }}>
      <StripePayForm
        payload={payload}
        clientSecret={clientSecret}
        onSuccess={onSuccess}
        onError={onError}
      />
    </Elements>
  );
}
